import { Button } from "@studyplus/boron-ui";
import { Field, Form, Formik } from "formik";
import { FC, useState } from "react";
import * as yup from "yup";
import { KarteContentMaxLength } from "../../../../consts/KarteContentMaxLength";
import { LineOutgoingMessageMaxLength } from "../../../../consts/LineOutgoingMessageMaxLength";
import TimeHelper from "../../../../helpers/TimeHelper";
import { useFlashMessage } from "../../../../hooks/useFlashMessage";
import KarteInterface from "../../../../interfaces/KarteInterface";
import Icon from "../../../atoms/Icon";
import Label from "../../../atoms/Label";
import { Modal } from "../../../atoms/Modal";
import Textarea from "../../../atoms/Textarea";
import styles from "./styles.scss";
import { usePostKarteShare } from "./usePostKarteShare";

type Props = {
  karte: KarteInterface;
};

interface Values {
  message: string;
}

const KarteShareButton: FC<Props> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: postKarteShare, isPending } = usePostKarteShare();
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();

  if (props.karte.student.lineConnectionStatus !== "connected") {
    return null;
  }

  const openModal = () => {
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
  };

  const date = TimeHelper.japaneseFormatFullDate(props.karte.createdAt);
  const placeholder = `${props.karte.student.fullName}さんの${date}のカルテをお送りします。`;

  const handleSubmit = (values: Values) => {
    const { karte } = props;
    const message =
      stringLength(values.message) === 0 ? placeholder : values.message;

    postKarteShare(
      {
        studentId: karte.student.id,
        karteId: karte.id,
        message,
      },
      {
        onSuccess: () => {
          showSuccessMessage("カルテを送信しました");
          closeModal();
        },
        onError: () => {
          showErrorMessage("カルテの送信に失敗しました");
        },
      },
    );
  };

  return (
    <>
      <button className={styles.button} onClick={openModal}>
        <Icon name="icon-line" className={styles.lineIcon} />
      </button>
      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        className={styles.modal}
      >
        <Modal.Header onClose={closeModal}>
          <h2>{props.karte.student.fullName}のカルテを送信</h2>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => {
              const messageLength = stringLength(formik.values.message);
              const karteCommentLength = stringLength(props.karte.comment);

              return (
                <Form>
                  <div className={styles.messageLabelLine}>
                    <Label className={styles.messageLabel}>メッセージ</Label>
                    <div className={styles.messageLength}>
                      {messageLength}/{LineOutgoingMessageMaxLength}字
                    </div>
                  </div>
                  <Field
                    name="message"
                    component={Textarea}
                    placeholder={placeholder}
                    className={styles.message}
                  />
                  <div className={styles.messageLabelLine}>
                    <Label className={styles.messageLabel}>カルテ内容</Label>
                    <div className={styles.messageLength}>
                      {karteCommentLength}/{KarteContentMaxLength}字
                    </div>
                  </div>
                  <Textarea
                    className={styles.karteComment}
                    placeholder={placeholder}
                    value={props.karte.comment}
                    readOnly={true}
                  />
                  <div className={styles.notice}>
                    ※内容の変更は、カルテの「編集」から変更できます。
                  </div>
                  <div className={styles.notice}>
                    ※カルテの添付画像・添付ファイルは、送信されません。カルテ内容のテキストのみ送信されます。
                  </div>
                  <div className={styles.submitButton}>
                    <Button
                      disabled={isPending || !formik.isValid}
                      type="submit"
                    >
                      送信
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

const stringLength = (str: string | null) => {
  if (!str) {
    return 0;
  }
  return Array.from(str).length;
};

const initialValues: Values = {
  message: "",
};

const validationSchema = yup.object().shape({
  message: yup.string().trim().min(0).max(LineOutgoingMessageMaxLength),
});

export default KarteShareButton;
